import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { SERVICES } from "@/lib/i18n";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { lang, t } = useI18n();
  const services = pickLang(SERVICES, lang);

  const NAV_LINKS = [
    { label: t("nav.about"), to: "/about" },
    { label: t("nav.industries"), to: "/industries" },
    { label: t("nav.services"), to: "/services", hasDropdown: true },
    { label: t("nav.stb"), to: "/set-top-box" },
    { label: t("nav.pricing"), to: "/pricing" },
    { label: t("nav.clients"), to: "/clients" },
    { label: t("nav.blog"), to: "/blog" },
    { label: t("nav.contact"), to: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setServicesOpen(false); setMobileServicesOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#2A3B5C]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-3">
        <Logo testId="nav-logo-link" />

        <nav className="hidden xl:flex items-center gap-0.5">
          {NAV_LINKS.map((l, i) => {
            if (l.hasDropdown) {
              return (
                <div
                  key={i}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <NavLink
                    to={l.to}
                    data-testid="nav-link-services"
                    className={({ isActive }) =>
                      `px-3 py-2 text-[13px] rounded-full transition-colors whitespace-nowrap inline-flex items-center gap-1 ${
                        isActive || servicesOpen ? "text-white bg-white/10" : "text-slate-200 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown size={13} className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
                  </NavLink>
                  {servicesOpen && (
                    <div
                      data-testid="nav-services-dropdown"
                      className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-[560px]"
                    >
                      <div className="rounded-2xl border border-white/10 bg-[#324567] backdrop-blur-xl shadow-2xl p-3 grid grid-cols-2 gap-1">
                        {services.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            data-testid={`nav-services-${s.slug}`}
                            className="px-3 py-2.5 rounded-xl text-[13px] text-slate-100 hover:bg-white/[0.07] hover:text-white transition-colors leading-tight"
                          >
                            {s.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={i}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-[13px] rounded-full transition-colors whitespace-nowrap ${
                    isActive ? "text-white bg-white/10" : "text-slate-200 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <LanguageSwitcher />
          <Link to="/demo" data-testid="nav-cta-demo" className="btn-primary !py-2.5 !px-5 text-sm">
            {t("nav.demo")} <ArrowRight size={16} />
          </Link>
        </div>

        <div className="xl:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <button data-testid="nav-mobile-toggle" className="p-2 text-white" onClick={() => setOpen((s) => !s)} aria-label="Toggle menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="xl:hidden border-t border-white/10 bg-[#2A3B5C]/95 backdrop-blur-xl max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-1">
            {NAV_LINKS.map((l, i) => {
              if (l.hasDropdown) {
                return (
                  <div key={i}>
                    <div className="flex items-stretch">
                      <NavLink
                        to={l.to}
                        data-testid="mobile-nav-link-services"
                        className={({ isActive }) => `flex-1 px-4 py-3 rounded-xl text-base ${isActive ? "text-white bg-white/10" : "text-slate-200 hover:bg-white/5"}`}
                      >
                        {l.label}
                      </NavLink>
                      <button
                        data-testid="mobile-nav-services-toggle"
                        onClick={() => setMobileServicesOpen((s) => !s)}
                        className="px-3 text-slate-300 hover:text-white"
                        aria-label="Expand services"
                      >
                        <ChevronDown size={18} className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                    {mobileServicesOpen && (
                      <div className="ml-4 mt-1 pl-3 border-l border-white/10 flex flex-col gap-0.5" data-testid="mobile-services-submenu">
                        {services.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            data-testid={`mobile-nav-services-${s.slug}`}
                            className="px-3 py-2 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5"
                          >
                            {s.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={i}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.to.replace("/", "") || "home"}`}
                  className={({ isActive }) => `px-4 py-3 rounded-xl text-base transition-colors ${isActive ? "text-white bg-white/10" : "text-slate-200 hover:bg-white/5"}`}
                >
                  {l.label}
                </NavLink>
              );
            })}
            <Link to="/demo" data-testid="mobile-nav-cta-demo" className="btn-primary mt-3 justify-center">
              {t("nav.demo")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
